import React, { useState, memo } from 'react'
import PropTypes from 'prop-types'
import { requestAPI } from '../../data/api'
import * as Icons from 'react-feather'
import loadUsers from './loadUsers'

function User({ user, clients }, context) {
	const [showClientPickers, setShowClientPickers] = useState(false)

	const userName = `${user.firstname} ${user.lastname}`
	const roleValue = user.isphotographer ? 'photographer' : user.permission_level

	const onDeleteClientFromUser = async (client_id, user_id) => {
		await deleteClientFromUser(client_id, user_id)
		loadUsers()
	}

	const deleteUser = async (userName, userId) => {
		if (
			!window.confirm(
				`${context.gettext('admin.settings.confirm_delete_user')}\n${userName}?`
			)
		)
			return
		await requestAPI(`/users/${userId}`, 'DELETE')
		loadUsers()
	}

	const addClients = async (client_id, user_id) => {
		if (client_id && user_id) {
			await addClientToUser(client_id, user_id)
			loadUsers()
		}
	}

	return (
		<div className='userCard'>
			<header className='userCard__header'>
				<div className='userCard__avatar'>
					{(user.firstname || '?')[0]}
					{(user.lastname || '?')[0]}
				</div>

				<div className='userCard__identity'>
					<h4 className='userCard__name' title={userName}>
						{user.firstname} {user.lastname}
					</h4>
					<div className='userCard__email' title={user.email}>
						{user.email}
					</div>
				</div>

				<div className='userCard__actions'>
					<div className='userCard__role'>
						<select
							id={`role-${user.user_id}`}
							className='userCard__select'
							value={roleValue}
							onChange={async (e) => {
								await updateUser({
									user_id: user.user_id,
									permission_level: e.target.value,
								})
								loadUsers()
							}}
						>
							<option value='customer'>Client</option>
							<option value='photographer'>Stringer</option>
							<option value='admin'>
								{context.gettext('admin.settings.role_administrator')}
							</option>
							<option value='agent'>
								{context.gettext('admin.settings.role_agent')}
							</option>
						</select>
					</div>

					<button
						type='button'
						className='iconBtn iconBtn--danger'
						onClick={() => deleteUser(userName, user.user_id)}
						title='Delete user'
					>
						<Icons.Trash2 size={16} />
					</button>
				</div>
			</header>

			<section className='userCard__body'>
				<div className='userCard__chips'>
					{user.clients.map((client) => (
						<span className='chip' key={client.client_id}>
							<span className='chip__label' title={client.name}>
								{client.name}
							</span>
							<button
								type='button'
								className='chip__remove'
								onClick={() =>
									onDeleteClientFromUser(client.client_id, user.user_id)
								}
								title='Remove client'
							>
								<Icons.X size={14} />
							</button>
						</span>
					))}
				</div>

				<div className='userCard__divider' />

				<div className='userCard__add'>
					<button
						type='button'
						className='btn'
						style={{ width: 150, textAlign: 'center' }}
						onClick={() => setShowClientPickers(!showClientPickers)}
					>
						{showClientPickers ? (
							<>
								<Icons.ChevronUp size={16} /> Hide
							</>
						) : (
							<>
								<Icons.Plus size={16} /> Link Partner
							</>
						)}
					</button>

					{showClientPickers && (
						<div
							id={`clientPickers-${user.user_id}`}
							className={`pickerWrap ${showClientPickers ? 'is-open' : ''}`}
						>
							<div>
								<small>Select partner (client) to link</small>
							</div>
							<div className='pickerRow'>
								<label
									htmlFor={`mainClients-${user.user_id}`}
									className='pickerLabel'
								>
									Main clients
								</label>
								<select
									id={`mainClients-${user.user_id}`}
									className='pickerSelect'
									value=''
									onChange={(e) => addClients(e.target.value, user.user_id)}
								>
									<option disabled value=''>
										Select…
									</option>
									{clients.mainClients &&
										clients.mainClients.map((client) => {
											const alreadyAssigned = user.clients.some(
												(c) => c.client_id === client.client_id
											)
											return (
												<option
													key={client.client_id}
													value={client.client_id}
													disabled={alreadyAssigned}
												>
													{client.name}
												</option>
											)
										})}
								</select>
							</div>

							<div className='pickerRow'>
								<label
									htmlFor={`scanClients-${user.user_id}`}
									className='pickerLabel'
								>
									New scanning clients
								</label>
								<select
									id={`scanClients-${user.user_id}`}
									className='pickerSelect'
									value=''
									onChange={(e) => addClients(e.target.value, user.user_id)}
								>
									<option disabled value=''>
										Select…
									</option>
									{clients.newScanningClients &&
										clients.newScanningClients.map((client) => {
											const alreadyAssigned = user.clients.some(
												(c) => c.client_id === client.client_id
											)
											return (
												<option
													key={client.client_id}
													value={client.client_id}
													disabled={alreadyAssigned}
												>
													{client.name}
												</option>
											)
										})}
								</select>
							</div>
						</div>
					)}
				</div>
			</section>
		</div>
	)
}

User.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

function updateUser(updatedUser) {
	return requestAPI('/authentication/update-user', 'POST', updatedUser)
}

function addClientToUser(client_id, user_id) {
	return requestAPI('/authentication/add-hub-to-user', 'POST', {
		user_id,
		client_id,
	})
}

function deleteClientFromUser(client_id, user_id) {
	return requestAPI('/authentication/delete-hub-from-user', 'POST', {
		user_id,
		client_id,
	})
}

export default memo(User)
